import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import {
    Map,
    MapPin,
    Users,
    UserRound,
    Church,
    Languages,
    GraduationCap,
    BriefcaseBusiness,
    Wallet,
    Heart,
    Home,
    Star,
    Moon,
    ShieldCheck,
    Ruler,
    Palette,
    Droplets,
    Globe,
    Building2,
    UserCog,
    Search,
    Plus,
    ChevronRight,
    Database,
    CheckCircle,
} from "lucide-react";

/*
|--------------------------------------------------------------------------
| MASTER CONFIGURATION
|--------------------------------------------------------------------------
*/

const masterItems = [
    {
        id: "state",
        title: "State",
        description:
            "Manage states used across member profiles.",
        icon: Map,
        count: 36,
        color: "blue",
    },

    {
        id: "district",
        title: "District",
        description:
            "Manage districts linked with states.",
        icon: MapPin,
        count: 38,
        color: "pink",
    },

    {
        id: "taluk",
        title: "Taluk",
        description:
            "Manage taluk and sub-district information.",
        icon: MapPin,
        count: 315,
        color: "purple",
    },

    {
        id: "community",
        title: "Community",
        description:
            "Manage matrimonial community categories.",
        icon: Users,
        count: 18,
        color: "green",
    },

    {
        id: "sub-community",
        title: "Sub Community",
        description:
            "Manage community sub-categories.",
        icon: UserRound,
        count: 42,
        color: "orange",
    },

    {
        id: "caste",
        title: "Caste",
        description:
            "Manage caste information.",
        icon: Users,
        count: 76,
        color: "red",
    },

    {
        id: "religion",
        title: "Religion",
        description:
            "Manage religion master values.",
        icon: Church,
        count: 12,
        color: "blue",
    },

    {
        id: "mother-tongue",
        title: "Mother Tongue",
        description:
            "Manage member language preferences.",
        icon: Languages,
        count: 28,
        color: "pink",
    },

    {
        id: "education",
        title: "Education",
        description:
            "Manage education qualifications.",
        icon: GraduationCap,
        count: 35,
        color: "purple",
    },

    {
        id: "profession",
        title: "Profession",
        description:
            "Manage profession categories.",
        icon: BriefcaseBusiness,
        count: 52,
        color: "green",
    },

    {
        id: "occupation",
        title: "Occupation",
        description:
            "Manage occupation types.",
        icon: BriefcaseBusiness,
        count: 64,
        color: "orange",
    },

    {
        id: "income",
        title: "Annual Income",
        description:
            "Manage annual income ranges.",
        icon: Wallet,
        count: 14,
        color: "red",
    },

    {
        id: "marital-status",
        title: "Marital Status",
        description:
            "Manage member marital status.",
        icon: Heart,
        count: 6,
        color: "blue",
    },

    {
        id: "family-type",
        title: "Family Type",
        description:
            "Manage family structure categories.",
        icon: Home,
        count: 6,
        color: "pink",
    },

    {
        id: "family-status",
        title: "Family Status",
        description:
            "Manage family status categories.",
        icon: Home,
        count: 5,
        color: "purple",
    },

    {
        id: "star",
        title: "Star / Nakshatra",
        description:
            "Manage horoscope star values.",
        icon: Star,
        count: 27,
        color: "orange",
    },

    {
        id: "rasi",
        title: "Rasi",
        description:
            "Manage 12 zodiac rasi values.",
        icon: Moon,
        count: 12,
        color: "green",
    },

    {
        id: "dosham",
        title: "Dosham",
        description:
            "Manage horoscope dosham types.",
        icon: ShieldCheck,
        count: 8,
        color: "red",
    },

    {
        id: "height",
        title: "Height",
        description:
            "Manage height values.",
        icon: Ruler,
        count: 15,
        color: "blue",
    },

    {
        id: "body-type",
        title: "Body Type",
        description:
            "Manage body type categories.",
        icon: UserRound,
        count: 6,
        color: "pink",
    },

    {
        id: "complexion",
        title: "Complexion",
        description:
            "Manage complexion categories.",
        icon: Palette,
        count: 8,
        color: "purple",
    },

    {
        id: "blood-group",
        title: "Blood Group",
        description:
            "Manage blood group values.",
        icon: Droplets,
        count: 8,
        color: "red",
    },

    {
        id: "country",
        title: "Country",
        description:
            "Manage country information.",
        icon: Globe,
        count: 195,
        color: "green",
    },

    {
        id: "city",
        title: "City",
        description:
            "Manage city and location information.",
        icon: Building2,
        count: 425,
        color: "orange",
    },

    {
        id: "branch-staff",
        title: "Branch Staff",
        description:
            "Manage branch staff members.",
        icon: UserCog,
        count: 34,
        color: "blue",
    },
];

/*
|--------------------------------------------------------------------------
| MASTER PAGE
|--------------------------------------------------------------------------
*/

function Masters() {
    const navigate = useNavigate();

    const [search, setSearch] = useState("");

    /*
    |--------------------------------------------------------------------------
    | FILTER MASTER LIST
    |--------------------------------------------------------------------------
    */

    const keyword = search.trim().toLowerCase();

const filteredMasters = !keyword
    ? masterItems
    : masterItems.filter(
          (item) =>
              item.title.toLowerCase().includes(keyword) ||
              item.description.toLowerCase().includes(keyword)
      );

    /*
    |--------------------------------------------------------------------------
    | OPEN MASTER
    |--------------------------------------------------------------------------
    */

    const handleOpenMaster = (master) => {
        navigate(
            `/super-admin/masters/${master.id}`
        );
    };

    /*
    |--------------------------------------------------------------------------
    | ADD MASTER
    |--------------------------------------------------------------------------
    */

    const handleAddMaster = () => {
        /*
         * You can later open a modal here to select
         * which master needs to be created.
         */

        navigate("/super-admin/masters/state");
    };

    /*
    |--------------------------------------------------------------------------
    | CLEAR SEARCH
    |--------------------------------------------------------------------------
    */

    const clearSearch = () => {
        setSearch("");
    };

    /*
    |--------------------------------------------------------------------------
    | TOTAL MASTER VALUES
    |--------------------------------------------------------------------------
    */

    const totalValues = masterItems.reduce(
    (total, item) => total + item.count,
    0
);

    /*
    |--------------------------------------------------------------------------
    | RENDER
    |--------------------------------------------------------------------------
    */

    return (
        <div className="masters-page">

            {/* =========================================================
                PAGE HEADER
            ========================================================= */}

            <div className="masters-page-header">

                <div className="masters-heading">

                    <div className="masters-heading-icon">
                        <Database size={22} />
                    </div>

                    <div>
                        <h1>Masters</h1>

                        <p>
                            Manage master data used throughout
                            Jodi Point Matrimonial Services.
                        </p>
                    </div>

                </div>

                <button
                    type="button"
                    className="masters-add-btn"
                    onClick={handleAddMaster}
                >
                    <Plus size={17} />

                    Add Master
                </button>

            </div>

            {/* =========================================================
                SUMMARY CARDS
            ========================================================= */}

            <div className="masters-summary-grid">

                {/* TOTAL MASTERS */}

                <div className="master-summary-card">

                    <div className="master-summary-icon blue">
                        <Database size={21} />
                    </div>

                    <div className="master-summary-content">

                        <strong>
                            {masterItems.length}
                        </strong>

                        <span>
                            Total Masters
                        </span>

                    </div>

                </div>

                {/* ACTIVE */}

                <div className="master-summary-card">

                    <div className="master-summary-icon green">
                        <CheckCircle size={21} />
                    </div>

                    <div className="master-summary-content">

                        <strong>
                            {masterItems.length}
                        </strong>

                        <span>
                            Active Masters
                        </span>

                    </div>

                </div>

                {/* TOTAL VALUES */}

                <div className="master-summary-card">

                    <div className="master-summary-icon pink">
                        <MapPin size={21} />
                    </div>

                    <div className="master-summary-content">

                        <strong>
                            {totalValues.toLocaleString()}
                        </strong>

                        <span>
                            Total Master Values
                        </span>

                    </div>

                </div>

            </div>

            {/* =========================================================
                TOOLBAR
            ========================================================= */}

            <div className="masters-toolbar">

                <div className="masters-toolbar-left">

                    <div className="masters-toolbar-title">
                        <h2>
                            Master Data
                        </h2>

                        <span>
                            {filteredMasters.length} Masters
                        </span>
                    </div>

                </div>

                <div className="masters-toolbar-right">

                    <div className="masters-search">

                        <Search size={16} />

                        <input
                            type="text"
                            value={search}
                            placeholder="Search master..."
                            onChange={(e) =>
                                setSearch(
                                    e.target.value
                                )
                            }
                        />

                        {search && (
                            <button
                                type="button"
                                className="masters-clear-search"
                                onClick={clearSearch}
                            >
                                ×
                            </button>
                        )}

                    </div>

                </div>

            </div>

            {/* =========================================================
                MASTER GRID
            ========================================================= */}

            {filteredMasters.length > 0 ? (
                <div className="masters-grid">

                    {filteredMasters.map(
                        (master) => {

                            const Icon =
                                master.icon;

                            return (
                                <div
                                    className="master-card"
                                    key={master.id}
                                    onClick={() =>
                                        handleOpenMaster(
                                            master
                                        )
                                    }
                                >

                                    {/* ICON */}

                                    <div
                                        className={`master-card-icon ${master.color}`}
                                    >
                                        <Icon size={21} />
                                    </div>

                                    {/* CONTENT */}

                                    <div className="master-card-content">

                                        <div className="master-card-title-row">

                                            <h3>
                                                {master.title}
                                            </h3>

                                            <span className="master-count">
                                                {master.count.toLocaleString()}
                                            </span>

                                        </div>

                                        <p>
                                            {
                                                master.description
                                            }
                                        </p>

                                        <div className="master-card-footer">

                                            <span>
                                                Manage
                                            </span>

                                            <ChevronRight
                                                size={16}
                                            />

                                        </div>

                                    </div>

                                </div>
                            );
                        }
                    )}

                </div>
            ) : (
                /* =====================================================
                   EMPTY SEARCH
                ===================================================== */

                <div className="masters-empty">

                    <div className="masters-empty-icon">
                        <Search size={30} />
                    </div>

                    <h3>
                        No Masters Found
                    </h3>

                    <p>
                        No master data matches your
                        search. Try another keyword.
                    </p>

                    <button
                        type="button"
                        onClick={clearSearch}
                    >
                        Clear Search
                    </button>

                </div>
            )}

        </div>
    );
}

export default Masters;